import { fmtNumber, fmtPctAbs, fmtRatio } from "../utils/formatters";
import { hslString } from "../utils/colors";

/**
 * Linha individual do funil. Mantém TODA a info centralizada dentro da barra
 * (icone+nome+legenda+valor/%+comparativo). Largura proporcional ao ratio com
 * piso mínimo pra texto caber sempre — ver FUNNEL_WIDTH_FLOOR em constants.js.
 *
 * Props agrupadas pra ficar dentro do limite de 5 (conventions.md §3):
 *   - stage:  definição da etapa (key, title, legend, Icon)
 *   - data:   { count, prevCount, delta, ratioCurrent, ratioPrevious }
 *   - style:  { width, hsl, layerLightness }
 */
export function FunnelBar({ stage, data, style }) {
  const { Icon } = stage;
  const { count, prevCount, delta, ratioCurrent, ratioPrevious } = data;
  const { width, hsl, layerLightness } = style;
  const lighter = hslString(hsl, layerLightness + 12);
  const darker = hslString(hsl, layerLightness - 10);
  const inset = hslString(hsl, layerLightness + 20, 0.5);
  const shadow = hslString(hsl, layerLightness, 0.35);
  const isPositive = delta >= 0;

  return (
    <div className="flex justify-center">
      <div
        className="relative rounded-2xl overflow-hidden transition-all duration-500 px-5 py-4 text-white text-center"
        style={{
          width: `${width}%`,
          minWidth: 260,
          background: `linear-gradient(135deg, ${lighter}, ${darker})`,
          boxShadow: `0 4px 14px ${shadow}, inset 0 1px 0 ${inset}`,
        }}
      >
        <div className="flex items-center justify-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4" />
          </div>
          <p className="text-base sm:text-lg font-extrabold leading-tight drop-shadow-sm">
            {stage.title}
          </p>
        </div>

        <p className="text-[11px] sm:text-xs opacity-85 leading-tight mb-2">{stage.legend}</p>

        <p className="text-2xl sm:text-3xl font-black tabular-nums leading-none drop-shadow">
          {fmtNumber(count)}{" "}
          <span className="text-xl sm:text-2xl opacity-90">/ {fmtRatio(ratioCurrent)}</span>
        </p>

        <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
          <p className="text-[11px] sm:text-xs opacity-85 tabular-nums">
            Anterior: <span className="font-semibold">{fmtNumber(prevCount)}</span> ({fmtRatio(ratioPrevious)})
          </p>
          <span
            className={`inline-flex items-center gap-0.5 text-[11px] sm:text-xs font-bold px-2 py-0.5 rounded-md whitespace-nowrap ${
              isPositive ? "bg-emerald-500/30 text-emerald-50" : "bg-red-500/30 text-red-50"
            }`}
          >
            {isPositive ? "↗" : "↘"} {fmtPctAbs(delta)}
          </span>
        </div>
      </div>
    </div>
  );
}
